import {useCallback, useEffect, useMemo, useState} from "react";
import {Empty, Select, Typography} from "antd";
import {t} from "i18next";
import ApiService from "../../../core/service/api-service";
import {matchesSearch} from "../../../core/utils/text-search";
import {defaultPanelFilter, PanelRangeBar} from "./PanelParts";
import DoctorPanel from "./DoctorPanel";
import ReporterPanel from "./ReporterPanel";

/**
 * Panom sekmesi. Kullanıcı kendi panosunu görür; Dashboard yetkisi olan (canPickUser) ayrıca bir
 * doktor ya da raportör seçip onun panosunu görebilir. Seçim yoksa kendi panosu (yöneticinin
 * kendisi doktor/raportör değilse seçim bekleyen boş durum).
 *
 * Seçenekler İstatistikler sekmesindeki süzgeçle aynı listelerden gelir (okuyan / yazan kullanıcılar).
 * İkisinde de olan kullanıcı iki grupta da görünür; hangi gruptan seçildiyse o pano açılır.
 */
export default function MyDashboard({isDoctor, isReporter, canPickUser}) {
    const [filter, setFilter] = useState(defaultPanelFilter)
    const [selected, setSelected] = useState(null)

    const fetcher = useCallback((f) => selected?.role === 'doctor'
            ? ApiService.Dashboard.UserReads({...f, userId: selected.userId})
            : ApiService.Dashboard.UserWrites({...f, userId: selected?.userId}),
        [selected])

    const picker = canPickUser && (
        <UserPicker
            value={selected}
            onChange={setSelected}
            placeholder={isDoctor || isReporter ? t('My dashboard') : t('Select user')}
        />
    )

    let body
    if (selected) {
        body = selected.role === 'doctor'
            ? <DoctorPanel key={selected.userId} filter={filter} fetcher={fetcher}/>
            : <ReporterPanel key={selected.userId} filter={filter} fetcher={fetcher}/>
    } else if (isDoctor || isReporter) {
        body = (
            <>
                {isDoctor && (
                    <>
                        {isReporter && <Typography.Title level={5}>{t('Read studies')}</Typography.Title>}
                        <DoctorPanel filter={filter}/>
                    </>
                )}
                {isReporter && (
                    <>
                        {isDoctor && <Typography.Title level={5} style={{marginTop: 24}}>{t('Written reports')}</Typography.Title>}
                        <ReporterPanel filter={filter}/>
                    </>
                )}
            </>
        )
    } else {
        body = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Select a user to see their dashboard')}/>
    }

    return (
        <>
            <PanelRangeBar onSearch={setFilter} extra={picker}/>
            {body}
        </>
    )
}

function UserPicker({value, onChange, placeholder}) {
    const [readers, setReaders] = useState([])
    const [writers, setWriters] = useState([])

    useEffect(() => {
        ApiService.UserManager.GetReadableUsers().then(res => res.success && setReaders(res.data || []))
        ApiService.UserManager.GetWritableUsers().then(res => res.success && setWriters(res.data || []))
    }, [])

    const options = useMemo(() => [
        {label: t('Doctor'), options: readers.map(u => ({label: u.visibleName, value: `doctor:${u.id}`}))},
        {label: t('Reporter'), options: writers.map(u => ({label: u.visibleName, value: `reporter:${u.id}`}))},
    ].filter(g => g.options.length), [readers, writers])

    return (
        <Select
            allowClear
            showSearch
            style={{minWidth: 240}}
            placeholder={placeholder}
            options={options}
            value={value ? `${value.role}:${value.userId}` : undefined}
            filterOption={(input, option) => matchesSearch(option?.label, input)}
            onChange={v => {
                if (!v) {
                    onChange(null)
                    return
                }
                const [role, userId] = v.split(':')
                onChange({role, userId})
            }}
        />
    )
}
